import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Button, Typography } from '@mui/material';
import {
  getManageableOperations,
  getOrganizationsForUser,
  saveGrower,
} from '../../lib/api';
import { Table } from '../../components/table/Table';
import { OperationDetails } from './OperationDetails';
import { useCurrentUser } from '../../hooks/use-current-user';
import { actions as growerActions } from '../../store/dux/grower-dux';
import { useGrowers } from '../../hooks/use-growers';

const newOperation = {
  operationName: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  officePhone: '',
  zipCode: '',
  organizationId: '',
  isActive: true,
};

export default function OperationManagement() {
  const [growers, setGrowers] = useState([]);
  const [currentGrower, setCurrentGrower] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [organizations, setOrganizations] = useState([]);
  const user = useCurrentUser();
  const dispatch = useDispatch();
  const { resetGrowerSelection } = useGrowers();

  const loadOperations = useCallback(
    () =>
      getOrganizationsForUser().then((orgs) => {
        setOrganizations(orgs);
        getManageableOperations().then((all) => {
          const growersWithOrg = all.map((grower) => {
            const organization = orgs.find(
              (org) => org.id === grower.organizationId,
            );
            return { ...grower, organization };
          });
          setGrowers(growersWithOrg);
          setIsLoading(false);
        });
      }),
    [],
  );

  useEffect(() => {
    loadOperations();
  }, [loadOperations]);

  return (
    <Box
      sx={{ p: 4, display: 'flex', flexDirection: 'column', height: '100%' }}
    >
      {buildHeader()}
      {buildTable()}
      {buildNewGrowerDialog()}
    </Box>
  );

  function buildNewGrowerDialog() {
    if (!currentGrower) return null;

    return (
      <OperationDetails
        operation={currentGrower}
        organizations={organizations}
        handleSave={save}
        handleClose={closeDialog}
        isAdmin={user.isAdmin}
      />
    );
  }

  function save(grower) {
    setIsLoading(true);

    saveGrower(grower).then(() => {
      loadOperations();
      dispatch(growerActions.loadOperations());
      closeDialog();
    });
  }

  function editGrower(grower, e) {
    e.preventDefault();
    setCurrentGrower(grower);
  }

  function closeDialog() {
    resetGrowerSelection();
    setCurrentGrower(null);
  }

  function buildHeader() {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography sx={{ display: 'inline-block', flex: 1 }} variant="h5">
          Operation Management
        </Typography>
        <Button
          onClick={() => setCurrentGrower(newOperation)}
          variant="contained"
          color="primary"
        >
          Add New Operation
        </Button>
      </Box>
    );
  }

  function buildTable() {
    return (
      <Table
        loading={isLoading}
        hideFooter={false}
        rows={growers}
        columns={buildTableColumns()}
        initialState={{
          pinnedColumns: { right: ['actions'] },
          sorting: {
            sortModel: [{ field: 'operationName', sort: 'asc' }],
          },
        }}
        localeText={{ footerTotalRows: 'Total Operations:' }}
      />
    );
  }

  function buildTableColumns() {
    return [
      {
        field: 'operationName',
        headerName: 'Name',
        flex: 1.25,
      },
      {
        field: 'addressLine1',
        headerName: 'Address',
        flex: 1.25,
      },
      {
        field: 'city',
        headerName: 'City',
        flex: 0.75,
      },
      {
        field: 'state',
        headerName: 'State',
        flex: 0.5,
      },
      {
        field: 'zipCode',
        headerName: 'ZIP',
        type: 'number',
        flex: 0.5,
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'organization',
        headerName: 'Organization',
        flex: 1.25,
        valueGetter: ({ row }) => row.organization?.name,
      },
      {
        field: 'isActive',
        headerName: 'Active',
        valueFormatter: ({ value }) => (value ? 'Yes' : 'No'),
      },
      {
        field: 'edit',
        headerName: 'Edit',
        sortable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => (
          <Button
            onClick={editGrower.bind(null, row)}
            variant="outlined"
            color="inherit"
            data-testid="edit-grower"
            disabled={
              row.organization?.isDefault || !row.organization?.isActive
            }
          >
            Edit
          </Button>
        ),
      },
    ].filter(Boolean);
  }
}
